/**
 * DDM Cart Tier Auto-Correction
 *
 * When a customer changes item quantity in the cart, ensures the correct
 * pricing tier variant is used. For example, reducing 8 items to 7 should
 * switch from the 8+ tier to the 4+ tier.
 *
 * Listens for 'cart:update' events dispatched by the theme after any
 * cart modification. Uses the cart data from the event (avoiding a
 * redundant /cart.js fetch) and caches product data for fast tier lookups.
 */
(function () {
  'use strict';

  // Guard against double-init (cart-products snippet renders in both drawer and cart page)
  if (window._ddmCartTiersInit) return;
  window._ddmCartTiersInit = true;

  var isSwapping = false;
  var checkTimer = null;
  var productCache = {};
  var pendingCart = null;

  document.addEventListener('cart:update', function (event) {
    var source = '';
    try { source = event.detail.data.source || ''; } catch (e) { /* ignore */ }

    if (isSwapping) return;
    if (source === 'ddm-cart-tiers' || source === 'ddm-consolidation') return;

    // Grab cart data from the event if available (avoids /cart.js fetch)
    try {
      if (event.detail && event.detail.resource && event.detail.resource.items) {
        pendingCart = event.detail.resource;
      }
    } catch (e) { /* ignore */ }

    clearTimeout(checkTimer);
    checkTimer = setTimeout(checkCartTiers, 50);
  });

  function fetchProduct(handle) {
    if (productCache[handle]) {
      return Promise.resolve(productCache[handle]);
    }
    return fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        productCache[handle] = product;
        return product;
      })
      .catch(function () { return null; });
  }

  function getOptionName(opt) {
    if (typeof opt === 'string') return opt;
    if (opt && typeof opt === 'object' && opt.name) return opt.name;
    return '';
  }

  function getCart() {
    if (pendingCart) {
      var cart = pendingCart;
      pendingCart = null;
      return Promise.resolve(cart);
    }
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  function checkCartTiers() {
    if (isSwapping) return;

    getCart()
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) return;

        // Group all cart items by product handle
        var byHandle = {};
        for (var i = 0; i < cart.items.length; i++) {
          var h = cart.items[i].handle;
          if (!byHandle[h]) byHandle[h] = [];
          byHandle[h].push(cart.items[i]);
        }

        // Fetch product data for each unique handle (cached after first call)
        var handles = Object.keys(byHandle);
        var fetches = [];
        for (var i = 0; i < handles.length; i++) {
          fetches.push(fetchProduct(handles[i]));
        }

        return Promise.all(fetches).then(function (products) {
          var allUpdates = {};
          var needsSwap = false;

          for (var p = 0; p < products.length; p++) {
            if (!products[p]) continue;
            var product = products[p];
            var items = byHandle[handles[p]];

            // Find "Quantity" option index — skip products without one
            var qtyOptIdx = -1;
            for (var i = 0; i < product.options.length; i++) {
              if (getOptionName(product.options[i]).toLowerCase() === 'quantity') {
                qtyOptIdx = i;
                break;
              }
            }
            if (qtyOptIdx === -1) continue;

            // Build tier map using option1/option2/option3 (guaranteed by Shopify)
            var optionKey = 'option' + (qtyOptIdx + 1);
            var tiers = [];
            var tierVariantIds = {};
            for (var i = 0; i < product.variants.length; i++) {
              var v = product.variants[i];
              var tierQty = parseInt(v[optionKey], 10);
              if (!isNaN(tierQty)) {
                tiers.push({ quantity: tierQty, variantId: v.id });
                tierVariantIds[v.id] = true;
              }
            }
            tiers.sort(function (a, b) { return a.quantity - b.quantity; });
            if (tiers.length === 0) continue;

            // Sum qty across all tier line items for this product
            var totalQty = 0;
            for (var i = 0; i < items.length; i++) {
              if (tierVariantIds[items[i].variant_id]) {
                totalQty += items[i].quantity;
              }
            }
            if (totalQty === 0) continue;

            // Find best tier for current total quantity
            var bestTier = tiers[0];
            for (var i = 0; i < tiers.length; i++) {
              if (totalQty >= tiers[i].quantity) bestTier = tiers[i];
            }

            // Check if already correct
            var tierLineItems = [];
            for (var i = 0; i < items.length; i++) {
              if (tierVariantIds[items[i].variant_id]) {
                tierLineItems.push(items[i]);
              }
            }
            if (tierLineItems.length === 1 && tierLineItems[0].variant_id === bestTier.variantId) {
              continue;
            }

            // Swap — zero all tier variants, set correct one
            needsSwap = true;
            for (var i = 0; i < tiers.length; i++) {
              allUpdates[tiers[i].variantId] = 0;
            }
            allUpdates[bestTier.variantId] = totalQty;
          }

          if (!needsSwap) return;

          isSwapping = true;
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: allUpdates })
          })
            .then(function () {
              isSwapping = false;
              document.dispatchEvent(new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  data: {
                    source: 'ddm-cart-tiers'
                  }
                }
              }));
            })
            .catch(function (err) {
              isSwapping = false;
              console.error('DDM cart tier swap error:', err);
            });
        });
      })
      .catch(function (err) {
        console.error('DDM cart tier check error:', err);
      });
  }

  // Pre-warm product cache for items already in cart
  fetch('/cart.js')
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      if (!cart || !cart.items) return;
      var seen = {};
      for (var i = 0; i < cart.items.length; i++) {
        var h = cart.items[i].handle;
        if (!seen[h]) {
          seen[h] = true;
          fetchProduct(h);
        }
      }
    })
    .catch(function () { /* ignore */ });
})();
